import { useNavigate } from "react-router";
import { LogOut } from "lucide-react";

import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { ValueList } from "@/components/data/ValueList";
import { formatDateTime } from "@/lib/format";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useMyProfile } from "@/features/profile/hooks";
import { CrossedFieldList } from "@/features/profile/CrossedFieldList";
import { crossedFields } from "@/features/profile/reviewFlags";

function StatusShell({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  const { session, signOut } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-dvh">
      <header className="px-4 pt-4 sm:px-6">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-4 rounded-lg bg-surface px-5 py-4 ring-1 ring-inset ring-line">
          <div className="min-w-0">
            <p className="truncate text-md font-extrabold tracking-tight text-fg">
              First Gulf Exchange
            </p>
            <p className="truncate text-xs text-fg-subtle">
              {session?.user.full_name_en ?? ""}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            icon={<LogOut className="size-4" />}
            onClick={async () => {
              await signOut();
              navigate("/login", { replace: true });
            }}
          >
            Sign out
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
        <h1 className="text-2xl font-semibold tracking-tight text-fg">{title}</h1>
        <div className="mt-4">{children}</div>
      </main>
    </div>
  );
}

/** Shown while HR reviews a submitted profile. */
export function PendingRoute() {
  const { data: profile } = useMyProfile();

  return (
    <StatusShell title="Your profile is with HR">
      <Alert tone="info">
        Nothing more is needed from you. HR will email you when your account is approved, usually
        within a working day.
      </Alert>

      {profile ? (
        <section className="mt-8">
          <h2 className="text-md font-semibold text-fg">What you submitted</h2>
          <ValueList
            className="mt-2"
            rows={[
              { label: "Name", value: profile.full_name_en },
              { label: "Position", value: profile.position_title },
              { label: "Mobile", value: profile.personal_mobile, identifier: true },
              { label: "Emirates ID", value: profile.emirates_id_number, identifier: true },
              { label: "Declaration accepted", value: formatDateTime(profile.declaration_accepted_at) },
            ]}
          />
          <p className="mt-4 text-base text-fg-muted">
            Spotted a mistake? Ask HR to send your profile back so you can correct it.
          </p>
        </section>
      ) : null}
    </StatusShell>
  );
}

/** Shown when HR sends a profile back for changes. */
export function RejectedRoute() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const { data: profile } = useMyProfile();
  // The fields HR crossed, each with what is wrong. HR may have said it all
  // with the crosses and left no note, or written a note as well.
  const crossed = crossedFields(profile?.review_flags);
  const note = session?.user.review_note;

  return (
    <StatusShell title="Your profile needs changes">
      {note || crossed.length === 0 ? (
        <Alert tone="warning" title="What HR asked for">
          {note ?? "HR did not leave a note. Contact them for details."}
        </Alert>
      ) : null}

      {crossed.length > 0 ? (
        <section className="mt-6">
          <h2 className="text-md font-semibold text-fg">
            {crossed.length === 1
              ? "One detail to put right"
              : `${crossed.length} details to put right`}
          </h2>
          <p className="mt-1 text-base text-fg-muted">
            HR marked these as wrong. Open each one, correct it, and submit again.
          </p>
          <div className="mt-2 rounded-md bg-alert-50 px-4 py-3 text-base text-alert-700">
            <CrossedFieldList fields={crossed} />
          </div>
        </section>
      ) : null}

      <p className="mt-6 text-base text-fg-muted">
        Make the changes and submit again. Everything you already filled in has been kept.
      </p>

      <Button
        variant="primary"
        size="lg"
        className="mt-4"
        onClick={() => navigate("/onboarding/review")}
      >
        Update my profile
      </Button>
    </StatusShell>
  );
}

/** Grouped so the router imports one symbol for both status pages. */
export const AccountStatusRoutes = { PendingRoute, RejectedRoute };
